import { Chip, type ChipProps } from '@mui/material';
import {
  bedrijfsrelatieLabels,
  cvVerwerkingsstatusLabels,
  gebruikersstatusLabels,
  kandidaatstatusLabels,
  matchbeoordelingLabels,
  vacaturestatusLabels,
  voordrachtfaseLabels,
  voordrachtuitkomstLabels,
} from '../helpers/labels.ts';

export type Statussoort =
  'kandidaat' | 'vacature' | 'fase' | 'uitkomst' | 'relatie' | 'beoordeling' | 'verwerking' | 'gebruiker';

type Kleur = NonNullable<ChipProps['color']>;

const labels: Record<Statussoort, Record<string, string>> = {
  kandidaat: kandidaatstatusLabels,
  vacature: vacaturestatusLabels,
  fase: voordrachtfaseLabels,
  uitkomst: voordrachtuitkomstLabels,
  relatie: bedrijfsrelatieLabels,
  beoordeling: matchbeoordelingLabels,
  verwerking: cvVerwerkingsstatusLabels,
  gebruiker: gebruikersstatusLabels,
};

const kleuren: Record<Statussoort, Record<string, Kleur>> = {
  kandidaat: { nieuw: 'info', actief: 'success', geplaatst: 'primary', inactief: 'default' },
  vacature: { concept: 'default', open: 'success', gepauzeerd: 'warning', vervuld: 'primary', gesloten: 'default' },
  fase: { voorbereiding: 'default', voorgesteld: 'info', gesprek: 'warning', aanbod: 'secondary', afgerond: 'default' },
  uitkomst: { lopend: 'info', geplaatst: 'success', afgewezen: 'error', teruggetrokken: 'default' },
  relatie: { prospect: 'info', klant: 'success', oud_klant: 'default' },
  beoordeling: { onbeoordeeld: 'default', goed: 'success', twijfel: 'warning', afgewezen: 'error' },
  verwerking: { wachtrij: 'default', bezig: 'info', verwerkt: 'success', mislukt: 'error' },
  gebruiker: { in_afwachting: 'warning', actief: 'success', gedeactiveerd: 'default' },
};

/**
 * Een chip met de status van iets, in een kleur die bij de status past.
 * @param props De soort status, de waarde en de grootte.
 * @returns De chip.
 */
export function StatusChip({
  soort,
  waarde,
  size = 'small',
}: {
  soort: Statussoort;
  waarde: string;
  size?: ChipProps['size'];
}) {
  const kleur = kleuren[soort][waarde] ?? 'default';
  return (
    <Chip
      size={size}
      label={labels[soort][waarde] ?? waarde}
      color={kleur}
      variant={kleur === 'default' ? 'outlined' : 'filled'}
    />
  );
}
